<template>
  <div class="mobile-menu">
    <div
      class="tw-fixed tw-left-0 tw-right-0 tw-top-[64px] tw-z-[99] tw-h-screen tw-bg-white tw-p-4 tw-transition-all tw-delay-[50ms] tw-duration-300"
      :class="{
        'tw-translate-x-0': menuIsOpen,
        'tw-translate-x-full': !menuIsOpen,
      }"
    >
      <q-scroll-area :visible="false" class="fit">
        <div class="tw-space-y-4">
          <div
            v-for="{ title, items } in sections"
            class="tw-flex tw-flex-col tw-gap-2"
          >
            <div v-if="title" class="tw-mb-2 tw-px-4 tw-text-gray-500">
              {{ title }}
            </div>

            <menu-link v-for="(item, key) in items" :key="key" :item="item" />
          </div>
        </div>
      </q-scroll-area>
    </div>
  </div>
</template>

<script setup>
import MenuLink from "components/layout/MenuLink.vue";
import { isDevelopmentEnvironment } from "composables/useHelpers";
import { storeToRefs } from "pinia";
import { SENTRY_FEEDBACK_BUTTON_AS_MENU_ITEM } from "plugins/sentry";
import { useMenuStore } from "store/modules/menuStore.js";
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useClassroomNavigationStore } from "classroom/stores/index.ts";
import { HOME_ROUTE_NAMES } from "home/routes/routeNames";
import { MENU_OWN_SECTION_ROUTE_NAMES } from "shared/constants";

// menuStore
const menuStore = useMenuStore();
const { closeMenu, supportMenuItem, logoutMenuItem } = menuStore;
const {
  menuItems,
  menuIsOpen,
  organiseItems,
  manageItems,
  profileItems,
  sentryFeedbackMenuItem,
} = storeToRefs(menuStore);

const classroomNavigationStore = useClassroomNavigationStore();
const { menuGroups: classroomMenuGroups, canSeeClassroomMenu } = storeToRefs(
  classroomNavigationStore,
);

// Router
const route = useRoute();

const submenuIsOpen = ref([]);

onBeforeUnmount(() => {
  window.removeEventListener("resize", null);
});

watch(menuIsOpen, () => {
  if (menuIsOpen.value) {
    document.body.classList.add("tw-overflow-hidden");
  } else {
    document.body.classList.remove("tw-overflow-hidden");
  }
});

const sections = computed(() => {
  const PROFILE_TITLE = "Profiel";

  const homeMenuItems = menuItems.value.filter(
    (item) => item.name === HOME_ROUTE_NAMES.MAIN,
  );
  const meetingsMenuItems = menuItems.value.filter(
    (item) => item.name === "myMeetings",
  );
  const remainingMenuItems = menuItems.value.filter(
    (item) => MENU_OWN_SECTION_ROUTE_NAMES.includes(item.name) === false,
  );

  const items = [
    ...(homeMenuItems.length ? [{ title: null, items: homeMenuItems }] : []),
    ...(meetingsMenuItems.length
      ? [{ title: null, items: meetingsMenuItems }]
      : []),
    ...(canSeeClassroomMenu.value
      ? [{ title: null, items: classroomMenuGroups.value }]
      : []),
    { title: null, items: remainingMenuItems },
    ...(organiseItems.value.length
      ? [{ title: "Organiseren", items: organiseItems.value }]
      : []),
    ...(manageItems.value.length
      ? [{ title: "Beheren", items: manageItems.value }]
      : []),
    {
      title: PROFILE_TITLE,
      items: [...profileItems.value, supportMenuItem, logoutMenuItem],
    },
  ];

  if (isDevelopmentEnvironment || !SENTRY_FEEDBACK_BUTTON_AS_MENU_ITEM) {
    // Don't add sentry feedback in development
    return items;
  }

  // Add sentry feedback menu item above logout in profile section
  const foundItem = items.find(({ title }) => title === PROFILE_TITLE);

  if (!foundItem || !Array.isArray(foundItem?.items)) {
    return items;
  }

  foundItem.items.splice(
    foundItem.items.length - 1,
    0,
    sentryFeedbackMenuItem.value,
  );

  return items;
});

watch(
  () => route.fullPath,
  () => {
    submenuIsOpen.value = [];
  },
);
</script>
